import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Tag, Space, Input, message, Modal, Button, Descriptions, Checkbox, InputNumber, Statistic, Row, Col, Radio } from 'antd';
import { SearchOutlined, StockOutlined, WarningOutlined, DollarOutlined, PieChartOutlined, BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { accountApi, tradingApi, clientApi, groupApi, batchApi } from '../services/api';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

export default function GroupedPositionPage({ scopeClientIds = null } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tradeDisabled = !user || user.trade_enabled !== 1;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [clearModal, setClearModal] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [selectedClearIds, setSelectedClearIds] = useState([]);
  const [clearSearch, setClearSearch] = useState('');
  const [sellMode, setSellMode] = useState('full');
  const [buyClientSearch, setBuyClientSearch] = useState('');
  const [buyClientPage, setBuyClientPage] = useState(1);
  const [buyModal, setBuyModal] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyStockCode, setBuyStockCode] = useState('');
  const [buyStockName, setBuyStockName] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [buyQty, setBuyQty] = useState(100);
  const [buyMode, setBuyMode] = useState('custom');
  const [buyPriceMode, setBuyPriceMode] = useState('sell2'); // sell2=卖2价 | limit=限价
  const [buyQuote, setBuyQuote] = useState(null); // 股票行情（最新价/涨跌幅/今开等）
  const [selectedClients, setSelectedClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [buyGroupClients, setBuyGroupClients] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [onlineClients, setOnlineClients] = useState([]);
  const [groupClientSet, setGroupClientSet] = useState(null); // null=不过滤

  // 构建分组树：顶层分组（parent_id 为空）为一级，子分组作为 children 缩进显示
  // 每个节点的 client_id_list 累加自身 + 所有子孙（去重），使"子分组相加 = 总分组"
  const treeGroups = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined, selfClientIds: g.client_id_list || [] }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else {
        roots.push(map[g.id]);
      }
    });
    const accumulate = (node) => {
      let all = [...(node.selfClientIds || [])];
      let sub = 0;
      (node.children || []).forEach(ch => {
        sub += 1 + accumulate(ch);
        all = all.concat(ch._allClientIds || []);
      });
      const uniq = [];
      const seen = new Set();
      all.forEach(id => { if (!seen.has(id)) { seen.add(id); uniq.push(id); } });
      node._allClientIds = uniq;
      node._subCount = sub;
      node.client_id_list = uniq;
      return sub;
    };
    roots.forEach(accumulate);
    return roots;
  }, [groups]);

  // 树形渲染分组节点：顶层主分组，子分组缩进显示
  const renderGroupNode = (node, depth) => (
    <div key={node.id}>
      <div onClick={() => setSelectedGroup(node.id)}
        style={{ padding: '6px 8px', cursor: 'pointer', borderRadius: 4, marginBottom: 4, marginLeft: depth * 16,
          background: selectedGroup === node.id ? '#e6f7ff' : '#fafafa',
          border: selectedGroup === node.id ? '1px solid #91d5ff' : '1px solid #f0f0f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <span>
          {depth > 0 ? <Tag color="purple" style={{ marginRight: 4 }}>子</Tag> : null}
          {node.name}
        </span>
        <span style={{ fontSize: 11, color: '#999' }}>
          {(node.client_id_list || []).length}户
          {!node.parent_id && node._subCount > 0 ? `（含${node._subCount}子）` : ''}
        </span>
      </div>
      {(node.children || []).map(ch => renderGroupNode(ch, depth + 1))}
    </div>
  );


  const loadData = async () => {
    setLoading(true);
    try {
      const res = await accountApi.getGroupedPositions();
      if (res.success) setData(res.data || []);
    } catch (e) { message.error('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // 仅保留有在线客户的股票，且只显示在线客户（非管理员按分组过滤）
  const onlineData = data.map(d => ({
    ...d,
    clients: (d.clients || []).filter(c => c.online && (!groupClientSet || groupClientSet.has(c.client_id))),
  })).filter(d => d.clients.length > 0);
  const filtered = onlineData.filter(d =>
    !search || d.stock_code.includes(search) || d.stock_name.includes(search)
  );

  // 加载账户列表和在线客户端
  useEffect(() => {
    accountApi.getAll().then(r => { if (r.success) setAllAccounts(r.data || []); }).catch(() => {});
    clientApi.getOnline().then(r => { if (r.success) setOnlineClients(r.data || []); }).catch(() => {});
  }, []);

  // 加载分组客户端ID（非管理员只显示自己分组的数据）
  useEffect(() => {
    if (!user || user.is_super_admin === 1 || user.role === 'admin') {
      setGroupClientSet(null);
      return;
    }
    if (!user.group_id) { setGroupClientSet(new Set()); return; }
    groupApi.list().then(r => {
      if (!r.success) return;
      const myGroup = (r.data || []).find(g => g.id === user.group_id);
      if (myGroup?.client_ids) {
        setGroupClientSet(new Set(JSON.parse(myGroup.client_ids)));
      }
    }).catch(() => {});
  }, [user]);

  // 股票代码输入回调
  const onBuyStockCodeChange = useCallback(async (val) => {
    setBuyStockCode(val);
    setBuyStockName('');
    setBuyPrice('');
    setBuyQuote(null);
    if (val && val.length >= 6) {
      try {
        const res = await tradingApi.getQuote(val);
        if (res?.success && res.data) {
          setBuyStockName(res.data.stock_name || '');
          setBuyPrice(res.data.current_price != null ? String(res.data.current_price) : '');
          setBuyQuote(res.data);
        }
      } catch {}
    }
  }, []);

  // 刷新股票行情（最新价/涨跌幅/今日开盘价）
  const refreshBuyQuote = useCallback(async () => {
    if (!buyStockCode || buyStockCode.length < 6) { message.warning('请先输入股票代码'); return; }
    try {
      const res = await tradingApi.getQuote(buyStockCode);
      if (res?.success && res.data) {
        setBuyStockName(res.data.stock_name || buyStockName);
        setBuyPrice(res.data.current_price != null ? String(res.data.current_price) : buyPrice);
        setBuyQuote(res.data);
        message.success('行情已刷新');
      } else {
        message.warning('获取行情失败');
      }
    } catch {
      message.error('刷新行情失败');
    }
  }, [buyStockCode, buyStockName, buyPrice]);

  // 计算总金额（避免空字符串解析为NaN）
  const buyTotal = buyPrice && buyQty ? (parseFloat(buyPrice || '0') * buyQty) : 0;

  // 按 client_id 汇总可用资金
  const clientFunds = {};
  for (const ac of allAccounts) {
    clientFunds[ac.client_id] = (clientFunds[ac.client_id] || 0) + (ac.available || 0);
  }
  // 从 onlineClients API 获取所有在线客户列表
  const onlineClientMap = {};
  for (const cl of onlineClients) {
    if (cl.status !== 'online') continue;
    onlineClientMap[cl.id] = cl.name || cl.id;
  }
  const clientList = Object.keys(clientFunds).filter(id => onlineClientMap[id]).map(id => ({
    client_id: id, client_name: onlineClientMap[id], available: clientFunds[id],
  })).sort((a, b) => b.available - a.available);

  // 加载分组列表
  useEffect(() => {
    groupApi.list().then(r => { if (r.success) setGroups(r.data || []); }).catch(() => {});
  }, []);

  // 打开弹窗时重置
  useEffect(() => {
    if (!buyModal) { setSelectedGroup(''); setSelectedClients([]); setBuyGroupClients([]); }
  }, [buyModal]);

  // 切换分组时计算组内客户端（有 scopeClientIds 时直接使用范围客户端，隐藏分组树选择）
  useEffect(() => {
    const funds = {};
    for (const ac of allAccounts) { funds[ac.client_id] = (funds[ac.client_id] || 0) + (ac.available || 0); }
    let cids;
    if (scopeClientIds) {
      cids = scopeClientIds;
    } else {
      if (!selectedGroup) { setBuyGroupClients([]); setSelectedClients([]); return; }
      const g = groups.find(x => x.id === selectedGroup);
      cids = g?.client_id_list || [];
    }
    // 显示分组内所有客户端（含无可用资金的），有资金的可买入
    const clients = cids.map(cid => {
      const avail = funds[cid] || 0;
      return { client_id: cid, client_name: onlineClientMap[cid] || cid, available: avail };
    }).sort((a, b) => b.available - a.available);
    setBuyGroupClients(clients);
    setSelectedClients([]);
    setBuyClientPage(1);
  }, [selectedGroup, groups, allAccounts, scopeClientIds, buyModal]);

  const calcQty = (available) => {
    const price = parseFloat(buyPrice || '0');
    if (price <= 0) return 0;
    let funds;
    switch (buyMode) {
      case 'auto': funds = available * 0.99; break;
      case '1/2': funds = available * 0.99 * 0.5; break;
      case '1/3': funds = available * 0.99 / 3; break;
      case '1/4': funds = available * 0.99 * 0.25; break;
      default: return buyQty;
    }
    return Math.floor(funds / price / 100) * 100;
  };

  const handleBatchBuy = async () => {
    if (!selectedGroup) { message.warning('请选择分组'); return; }
    if (!buyStockCode || buyStockCode.length < 6) { message.warning('请输入6位股票代码'); return; }
    if (buyMode === 'custom' && (!buyQty || buyQty <= 0)) { message.warning('请输入购买数量'); return; }
    if (buyPriceMode === 'limit' && (!buyPrice || parseFloat(buyPrice) <= 0)) { message.warning('请输入限价'); return; }
    if (selectedClients.length === 0) { message.warning('请勾选要买入的客户端'); return; }
    setBuying(true);
    try {
      // 1. 创建买入批次（写入 batches + batch_records 表）
      const createRes = await batchApi.create({
        direction: 'buy',
        stock_code: buyStockCode, stock_name: buyStockName,
        buy_mode: buyMode, price_mode: buyPriceMode,
        price: buyPriceMode === 'limit' ? parseFloat(buyPrice) : 0,
        group_id: selectedGroup,
        quantity: buyQty, client_ids: selectedClients,
      });
      if (!createRes.success) {
        message.error(createRes.message || '创建批次失败');
        setBuying(false);
        return;
      }
      const batch = createRes.data.batch;
      // 2. 异步执行批次（后端 goroutine 并行买入）
      const execRes = await batchApi.execute(batch.id);
      if (!execRes.success) {
        message.error(execRes.message || '执行批次失败');
        setBuying(false);
        return;
      }
      setBuying(false);
      setBuyModal(false);
      message.success(`批量买入任务已启动 [${batch.batch_no}] ${batch.stock_name}(${batch.stock_code}) × ${batch.client_count}户`);
    } catch (e) {
      message.error('批量买入失败: ' + (e.response?.data?.message || e.message));
      setBuying(false);
    }
  };

  const refreshClearQuote = async () => {
    if (!clearModal || !clearModal.stock_code) return;
    try {
      const res = await tradingApi.getQuote(clearModal.stock_code);
      if (res?.success && res.data) {
        const newPrice = res.data.current_price;
        if (newPrice != null) {
          // 更新弹窗最新价，并同步刷新明细中每个客户端的现价
          setClearModal(prev => prev ? {
            ...prev,
            current_price: newPrice,
            clients: (prev.clients || []).map(c => ({ ...c, current_price: newPrice })),
          } : prev);
        } else {
          message.warning('接口未返回价格');
        }
      } else {
        message.error(res?.message || '获取行情失败');
      }
    } catch (e) {
      message.error('更新股价失败: ' + (e.response?.data?.message || e.message));
    }
  };

  // 打开清仓弹窗时自动拉取真实行情，避免沿用持仓快照旧价导致"最新价乱了"
  useEffect(() => {
    if (clearModal && clearModal.stock_code) {
      refreshClearQuote();
    }
  }, [clearModal && clearModal.stock_code]);

  const handleClearAll = async (stock) => {
    if (selectedClearIds.length === 0) { message.warning('请选择要清仓的客户端'); return; }
    setClearing(true);
    try {
      // 1. 创建清仓批次（写入 batches + batch_records 表）
      const createRes = await batchApi.create({
        direction: 'sell',
        stock_code: stock.stock_code, stock_name: stock.stock_name,
        buy_mode: sellMode, group_id: '', client_ids: selectedClearIds,
      });
      if (!createRes.success) {
        message.error(createRes.message || '创建清仓批次失败');
        setClearing(false);
        return;
      }
      const batch = createRes.data.batch;
      // 2. 异步执行批次（后端 goroutine 并行卖出）
      const execRes = await batchApi.execute(batch.id);
      if (!execRes.success) {
        message.error(execRes.message || '执行清仓批次失败');
        setClearing(false);
        return;
      }
      setClearing(false);
      setClearModal(null);
      message.success(`清仓任务已启动 [${batch.batch_no}] ${batch.stock_name}(${batch.stock_code}) × ${batch.client_count}户`);
      loadData();
    } catch (e) {
      message.error('清仓失败: ' + (e.response?.data?.message || e.message));
      setClearing(false);
    }
  };

  const expandedRowRender = (record) => (
    <div style={{ background: '#1a1a2e', borderRadius: 6, padding: 4 }}>
      <Table
        dataSource={record.clients}
        rowKey="client_id"
        size="small"
        pagination={false}
        columns={[
          { title: '客户端', dataIndex: 'client_name', width: 120, render: (v, r) => v ? (
            <a onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(r.client_id || '')}`)} style={{ color: '#1677ff' }}>{v}</a>
          ) : '-' },
          { title: '持有数量', dataIndex: 'quantity', width: 80, render: (v, r) => `${(v || 0).toLocaleString()}/${(r.available_qty || 0).toLocaleString()}` },
          { title: '成本价', dataIndex: 'cost_price', width: 90, render: v => v ? `¥${v.toFixed(3)}` : '-' },
          { title: '最新价', dataIndex: 'current_price', width: 90, render: v => v ? `¥${v.toFixed(2)}` : '-' },
          { title: '持有金额', dataIndex: 'amount', width: 110, render: v => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
          {
            title: '盈亏', width: 130,
            render: (_, r) => {
              if (!r.cost_price || r.cost_price <= 0) return '-';
              const pl = ((r.current_price || 0) - r.cost_price) * (r.quantity || 0);
              const plRatio = (r.current_price - r.cost_price) / r.cost_price;
              const color = pl >= 0 ? '#cf1322' : '#3f8600';
              return (
                <span style={{ color }}>
                  <span>{pl >= 0 ? '+' : ''}{pl.toFixed(2)}</span>
                  <br />
                  <span style={{ fontSize: 11 }}>{pl >= 0 ? '+' : ''}{(plRatio * 100).toFixed(2)}%</span>
                </span>
              );
            }
          },
        ]}
      />
    </div>
  );

  const columns = [
    { title: '股票代码', dataIndex: 'stock_code', width: 90 },
    { title: '股票名称', dataIndex: 'stock_name', width: 110, render: (v, r) => <a onClick={() => openKLine(r.stock_code, v)} style={{ cursor: 'pointer' }}><strong>{v}</strong></a> },
    { title: '最新价', dataIndex: 'current_price', width: 90, render: v => v ? <span style={{ fontWeight: 600, color: '#1890ff' }}>¥{v.toFixed(2)}</span> : '-' },
    { title: '持有客户', dataIndex: 'client_count', width: 80, render: v => <Tag>{v} 户</Tag> },
    { title: '总数量', dataIndex: 'total_quantity', width: 90, render: v => v?.toLocaleString() },
    { title: '总金额', dataIndex: 'total_amount', width: 120, render: v => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    {
      title: '操作', width: 80, fixed: 'right',
      render: (_, record) => (
        <Button size="small" danger icon={<WarningOutlined />} disabled={tradeDisabled}
          onClick={() => { setClearModal(record); setSellMode('full'); setSelectedClearIds((record.clients || []).filter(c => c.available_qty > 0).map(c => c.client_id)); }}
        >清仓</Button>
      ),
    },
  ];

  // ====== 图表数据 ======
  const chartStats = useMemo(() => {
    const totalValue = onlineData.reduce((s, g) => s + (g.clients || []).reduce((ss, c) => ss + ((c.current_price || 0) * c.quantity), 0), 0);
    let totalPL = 0, totalCost = 0;
    const pieData = onlineData.map(g => {
      const pl = g.clients?.reduce((s, c) => {
        if (!c.cost_price || c.cost_price <= 0) return s;
        return s + ((c.current_price || 0) - c.cost_price) * (c.quantity || 0);
      }, 0) || 0;
      const cost = g.clients?.reduce((s, c) => s + c.amount, 0) || 0;
      totalPL += pl;
      totalCost += cost;
      return { name: g.stock_name, value: cost, pl };
    }).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
    return { totalValue, totalPL, totalCost, plRatio: totalCost ? (totalPL / totalCost * 100) : 0, pieData };
  }, [data]);

  const pieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: ¥{c} ({d}%)' },
    series: [{
      type: 'pie', radius: ['35%', '60%'], avoidLabelOverlap: true,
      label: { show: true, formatter: '{b}', fontSize: 10 },
      emphasis: { label: { show: true, fontWeight: 'bold' } },
      data: chartStats.pieData.map(d => ({ name: d.name, value: d.value })),
    }],
  };

  const barOption = {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}<br/>盈亏: ¥${p[0].value.toFixed(2)}` },
    grid: { left: 60, right: 20, top: 10, bottom: 25 },
    xAxis: { type: 'category', data: chartStats.pieData.map(d => d.name), axisLabel: { fontSize: 9, rotate: 30 } },
    yAxis: { type: 'value', axisLabel: { formatter: '¥{value}' } },
    series: [{
      type: 'bar',
      data: chartStats.pieData.map(d => ({
        value: Math.round(d.pl * 100) / 100,
        itemStyle: { color: d.pl >= 0 ? '#cf1322' : '#3f8600' },
      })),
    }],
  };

  // ====== K线弹窗 ======
  const [klineModal, setKlineModal] = useState({ open: false, stockCode: '', stockName: '', data: [], loading: false });
  const openKLine = async (stockCode, stockName) => {
    setKlineModal({ open: true, stockCode, stockName, data: [], loading: true });
    try {
      const res = await tradingApi.getKLine(stockCode, 200, 60);
      if (res?.success) setKlineModal(prev => ({ ...prev, data: res.data || [], loading: false }));
      else setKlineModal(prev => ({ ...prev, loading: false }));
    } catch { setKlineModal(prev => ({ ...prev, loading: false })); }
  };
  const klineOption = (() => {
    if (!klineModal.data.length) return {};
    const closes = klineModal.data.map(d => d.close_price);
    const xData = klineModal.data.map((d, i) => i % Math.ceil(closes.length / 8) === 0 ? d.date?.slice(5) || '' : '');
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
      grid: [{ left: 50, right: 20, top: 40, height: 200 }, { left: 50, right: 20, top: 270, height: 60 }],
      xAxis: [{ type: 'category', data: xData, axisLabel: { fontSize: 9 } }, { type: 'category', gridIndex: 1, data: xData, axisLabel: { fontSize: 9 } }],
      yAxis: [{ type: 'value', scale: true, splitNumber: 4 }, { type: 'value', gridIndex: 1, splitNumber: 3, axisLabel: { formatter: '{value}' } }],
      series: [
        { name: 'K线', type: 'candlestick', data: klineModal.data.map(d => [d.open_price, d.close_price, d.low_price, d.high_price]), itemStyle: { color: '#cf1322', color0: '#3f8600', borderColor: '#cf1322', borderColor0: '#3f8600' } },
        { name: '成交量', type: 'bar', xAxisIndex: 1, yAxisIndex: 1, data: klineModal.data.map((d, i) => [i, d.volume, d.open_price <= d.close_price ? '#cf1322' : '#3f8600']).map(d => ({ value: d[1], itemStyle: { color: d[2] } })) },
      ],
    };
  })();

  return (
    <div>
    <Row gutter={12} style={{ marginBottom: 12 }}>
      <Col span={6}>
        <Card size="small"><Statistic title="持仓总市值" value={chartStats.totalValue} precision={2} prefix="¥" /></Card>
      </Col>
      <Col span={6}>
        <Card size="small"><Statistic title="持仓成本" value={chartStats.totalCost} precision={2} prefix="¥" /></Card>
      </Col>
      <Col span={6}>
        <Card size="small">
          <Statistic title="总盈亏"
            value={Math.abs(chartStats.totalPL)} precision={2}
            prefix={chartStats.totalPL >= 0 ? '+' : '-'}
            suffix={chartStats.totalPL >= 0 ? '↑' : '↓'}
            valueStyle={{ color: chartStats.totalPL >= 0 ? '#cf1322' : '#3f8600' }}
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small">
          <Statistic title="盈亏比例"
            value={Math.abs(chartStats.plRatio)} precision={2}
            suffix="%"
            valueStyle={{ color: chartStats.plRatio >= 0 ? '#cf1322' : '#3f8600' }}
          />
        </Card>
      </Col>
    </Row>
    <Row gutter={12} style={{ marginBottom: 12 }}>
      <Col span={10}>
        <Card size="small" title={<Space><PieChartOutlined />持仓分布</Space>} style={{ height: 240 }}>
          <div style={{ height: 180 }}><ReactEChartsCore option={pieOption} style={{ height: 180 }} /></div>
        </Card>
      </Col>
      <Col span={14}>
        <Card size="small" title="盈亏分布（按股票）" style={{ height: 240 }}>
          <div style={{ height: 180 }}><ReactEChartsCore option={barOption} style={{ height: 180 }} /></div>
        </Card>
      </Col>
    </Row>
    <Card
      title={<Space><StockOutlined /> 持仓分组（在线） <Tag>{onlineData.length} 只股票</Tag>
        <Button size="small" type="primary" icon={<DollarOutlined />} disabled={tradeDisabled} onClick={() => { setBuyPriceMode('sell2'); setBuyModal(true); }}>卖2价批量买入</Button>
        <Button size="small" icon={<DollarOutlined />} disabled={tradeDisabled} onClick={() => { setBuyPriceMode('limit'); setBuyModal(true); }}>限价批量买入</Button></Space>}
      size="small"
      extra={
        <Input
          size="small" prefix={<SearchOutlined />}
          placeholder="搜索股票代码/名称"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }} allowClear
        />
      }
    >
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="stock_code"
        loading={loading}
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: t => `共 ${t} 只股票` }}
        expandable={{
          expandedRowRender,
          rowExpandable: () => true,
        }}
      />

      <Modal
        title={<Space><WarningOutlined style={{ color: '#faad14' }} /> 清仓确认</Space>}
        open={!!clearModal}
        onCancel={() => !clearing && setClearModal(null)}
        footer={[
          <Button key="cancel" onClick={() => setClearModal(null)} disabled={clearing}>取消</Button>,
          <Button key="confirm" type="primary" danger loading={clearing}
            onClick={() => {
              // 若当前为总亏损状态，弹出亏损确认框，确认后才执行清仓
              const totalPL = (clearModal?.clients || []).reduce((s, c) => s + (c.profit_loss || 0), 0);
              if (totalPL < 0) {
                const totalCost = (clearModal?.clients || []).reduce((s, c) => s + (c.amount || 0), 0);
                const plRatio = totalCost ? (totalPL / totalCost * 100) : 0;
                Modal.confirm({
                  title: '⚠ 当前为亏损状态',
                  icon: <WarningOutlined style={{ color: '#faad14' }} />,
                  content: (
                    <div>
                      <p>当前 <b>{clearModal?.stock_name}({clearModal?.stock_code})</b> 持仓处于<b style={{ color: '#3f8600' }}>亏损</b>状态：</p>
                      <p style={{ fontSize: 16, margin: 0 }}>
                        总盈亏 <b style={{ color: '#3f8600' }}>{totalPL.toFixed(2)}</b> 元
                        （<b style={{ color: '#3f8600' }}>{plRatio.toFixed(2)}%</b>）
                      </p>
                      <p style={{ marginBottom: 0, color: '#666' }}>是否确认仍以买二价清仓 {selectedClearIds.length} 户客户？</p>
                    </div>
                  ),
                  okText: '确认清仓',
                  cancelText: '取消',
                  okButtonProps: { danger: true },
                  onOk: () => handleClearAll(clearModal),
                });
              } else {
                handleClearAll(clearModal);
              }
            }}
          >确认清仓（{selectedClearIds.length} 户）</Button>,
        ]}
        width={1040}
      >
        {clearModal && (
          <div>
            {(() => {
              const totalPL = clearModal.clients.reduce((s, c) => s + (c.profit_loss || 0), 0);
              const totalCost = clearModal.clients.reduce((s, c) => s + c.amount, 0);
              const plRatio = totalCost ? (totalPL / totalCost * 100) : 0;
              return (
              <Descriptions size="small" column={4} style={{ marginBottom: 12 }}>
                <Descriptions.Item label="股票">{clearModal.stock_name}({clearModal.stock_code})</Descriptions.Item>
                <Descriptions.Item label="最新价">
                  <span style={{ color: '#1890ff', fontWeight: 600 }}>¥{clearModal.current_price?.toFixed(2)}</span>
                  <Button size="small" icon={<ReloadOutlined />} onClick={refreshClearQuote} style={{ marginLeft: 8 }}>更新股价</Button>
                </Descriptions.Item>
                <Descriptions.Item label="总/选客户">{clearModal.clients.length}/{selectedClearIds.length}</Descriptions.Item>
                <Descriptions.Item label="可清数量">{clearModal.clients.reduce((s, c) => s + (c.available_qty > 0 ? c.available_qty : c.quantity), 0).toLocaleString()} 股</Descriptions.Item>
                <Descriptions.Item label="持仓成本" span={2}>¥{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                <Descriptions.Item label="总盈亏" span={2}>
                  <span style={{ color: totalPL >= 0 ? '#cf1322' : '#3f8600', fontWeight: 600, fontSize: 15 }}>
                    {totalPL >= 0 ? '+' : ''}{totalPL.toFixed(2)} ({plRatio >= 0 ? '+' : ''}{plRatio.toFixed(2)}%)
                  </span>
                </Descriptions.Item>
              </Descriptions>
              );
            })()}
            <Space style={{ marginBottom: 8 }}>
              <Radio.Group value={sellMode} onChange={e => setSellMode(e.target.value)} size="small">
                <Radio.Button value="full">全仓</Radio.Button>
                <Radio.Button value="1/2">1/2</Radio.Button>
                <Radio.Button value="1/3">1/3</Radio.Button>
                <Radio.Button value="1/4">1/4</Radio.Button>
              </Radio.Group>
            </Space>
            <Input.Search placeholder="搜索客户端名称或ID" value={clearSearch} onChange={e => setClearSearch(e.target.value)} style={{ width: 260, marginBottom: 8 }} allowClear />
            <div style={{ marginBottom: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button size="small" onClick={() => setSelectedClearIds(clearModal.clients.filter(c => (c.available_qty || 0) > 0).map(c => c.client_id))}>全选</Button>
              <Button size="small" onClick={() => setSelectedClearIds(prev => {
                const allIds = clearModal.clients.filter(c => (c.available_qty || 0) > 0).map(c => c.client_id);
                return allIds.filter(id => !prev.includes(id)).concat(prev.filter(id => !allIds.includes(id)));
              })}>反选</Button>
              <span style={{ fontSize: 12, color: '#888' }}>已选 {selectedClearIds.length} 户</span>
            </div>
            <Table
              dataSource={clearModal.clients.filter(c => {
                if (!clearSearch) return true;
                const s = clearSearch.toLowerCase();
                return (c.client_id || '').toLowerCase().includes(s) || (c.client_name || '').toLowerCase().includes(s);
              })}
              rowKey="client_id"
              size="small"
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: t => `共 ${t} 户` }}
              columns={[
                { title: '', width: 40, render: (_, r) => <Checkbox checked={selectedClearIds.includes(r.client_id)} disabled={!r.available_qty} onChange={e => setSelectedClearIds(e.target.checked ? [...selectedClearIds, r.client_id] : selectedClearIds.filter(id => id !== r.client_id))} /> },
                { title: '客户端', dataIndex: 'client_name', width: 100, render: v => v || '-' },
                { title: '持有/可用', dataIndex: 'quantity', width: 80, render: (v, r) => `${(v || 0).toLocaleString()}/${(r.available_qty || 0).toLocaleString()}` },
                { title: '成本价', dataIndex: 'cost_price', width: 80, render: v => v ? `¥${v.toFixed(3)}` : '-' },
                { title: '现价', dataIndex: 'current_price', width: 70, render: v => v ? `¥${v.toFixed(2)}` : '-' },
                { title: '持有金额', dataIndex: 'amount', width: 100, render: v => `¥${(v || 0).toFixed(2)}` },
                {
                  title: '盈亏', width: 120,
                  render: (_, r) => {
                    if (!r.profit_loss) return '-';
                    const color = r.profit_loss >= 0 ? '#cf1322' : '#3f8600';
                    return <span style={{ color }}>
                      {r.profit_loss >= 0 ? '+' : ''}{r.profit_loss.toFixed(2)} ({(r.profit_loss_ratio * 100).toFixed(2)}%)
                    </span>;
                  }
                },
              ]}
            />
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#fff7e6', borderRadius: 4, fontSize: 13 }}>
              <WarningOutlined style={{ color: '#faad14', marginRight: 6 }} />
              将以 <strong>买二价</strong> 对 <strong>{clearModal.clients.length} 户客户</strong> 执行卖出清仓操作，请确认。
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={<Space><DollarOutlined /> {buyPriceMode === 'limit' ? '限价批量买入' : '卖2价批量买入'}</Space>}
        open={buyModal}
        onCancel={() => !buying && setBuyModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setBuyModal(false)} disabled={buying}>取消</Button>,
          <Button key="confirm" type="primary" loading={buying}
            onClick={handleBatchBuy}
          >提交买入 ({selectedClients.length} 户)</Button>,
        ]}
        width={750}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          {!scopeClientIds && (
          <div style={{ width: 180, border: '1px solid #f0f0f0', borderRadius: 4, padding: 8, maxHeight: 450, overflow: 'auto' }}>
            <div style={{ fontWeight: 500, marginBottom: 8, color: '#888' }}>选择分组（树形）</div>
            {treeGroups.map(root => renderGroupNode(root, 0))}
            {treeGroups.length === 0 && <div style={{ color: '#ccc', textAlign: 'center', padding: 16 }}>暂无分组</div>}
          </div>
          )}
          <div style={{ flex: 1 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Input size="small" placeholder="股票代码" value={buyStockCode}
                  onChange={e => onBuyStockCodeChange(e.target.value)} style={{ width: 120 }} maxLength={6} />
                <span style={{ fontWeight: 600, width: 130 }}>{buyStockName}</span>
                {/* 最新价 */}
                <span style={{ fontSize: 13 }}>最新价:
                  <b style={{ color: '#1890ff' }}>{buyQuote?.current_price != null ? buyQuote.current_price.toFixed(2) : '--'}</b>
                </span>
                {/* 涨跌幅 */}
                <span style={{ fontSize: 13 }}>涨跌幅:
                  <b style={{ color: (buyQuote?.change_percent || 0) >= 0 ? '#e74c3c' : '#27ae60' }}>
                    {buyQuote?.change_percent != null ? `${buyQuote.change_percent >= 0 ? '+' : ''}${buyQuote.change_percent.toFixed(2)}%` : '--'}
                  </b>
                </span>
                {/* 今日开盘价 */}
                <span style={{ fontSize: 13 }}>今开:
                  <b style={{ color: '#666' }}>{buyQuote?.open_price != null ? buyQuote.open_price.toFixed(2) : '--'}</b>
                </span>
                {/* 刷新行情 */}
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshBuyQuote}>刷新</Button>
              </Space>
              {buyPriceMode === 'limit' && (
                <Space>
                  <InputNumber size="small" min={0.01} step={0.01} placeholder="限价" precision={3}
                    value={buyPrice !== '' && buyPrice !== null ? parseFloat(buyPrice) : undefined}
                    onChange={v => setBuyPrice(v !== null && v !== undefined ? String(v) : '')} style={{ width: 120 }} />
                </Space>
              )}
              <Radio.Group value={buyMode} onChange={e => setBuyMode(e.target.value)} size="small">
                <Radio.Button value="custom">自定义</Radio.Button>
                <Radio.Button value="auto">仓位资金</Radio.Button>
                <Radio.Button value="1/2">1/2</Radio.Button>
                <Radio.Button value="1/3">1/3</Radio.Button>
                <Radio.Button value="1/4">1/4</Radio.Button>
              </Radio.Group>
              {buyMode === 'custom' && (
                <Space><span>数量：</span>
                  <InputNumber size="small" min={100} step={100} value={buyQty}
                    onChange={v => setBuyQty(v || 0)} style={{ width: 120 }} />
                </Space>
              )}
              {buyMode === 'custom' && buyPrice > 0 && buyQty > 0 && (
                <div style={{ background: '#f6f8fa', padding: '4px 8px', borderRadius: 4, fontSize: 13 }}>
                  预计金额：¥{(parseFloat(buyPrice) * buyQty).toFixed(2)}
                </div>
              )}
              {buyMode !== 'custom' && buyPrice > 0 && (
                <div style={{ background: '#fff7e6', padding: '4px 8px', borderRadius: 4, fontSize: 12, color: '#d46b08' }}>
                  按资金{['1/2','1/3','1/4'].includes(buyMode) ? buyMode : '全部'}自动算量
                </div>
              )}
            </Space>
            <div style={{ fontWeight: 500, margin: '8px 0 4px', color: '#888' }}>
              选择客户端
              {buyGroupClients.length > 0 && <span style={{ marginLeft: 8, fontSize: 12, color: '#1890ff' }}>已选 {selectedClients.length}/{buyGroupClients.length}</span>}
            </div>
            <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
              {!scopeClientIds && !selectedGroup
                ? <div style={{ color: '#ccc', textAlign: 'center', padding: 16 }}>请先选择左侧分组</div>
                : buyGroupClients.length === 0
                ? <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>该分组无可用资金的客户端</div>
                : <div>
                  <Input.Search placeholder="搜索客户端名称或ID" value={buyClientSearch} onChange={e => { setBuyClientSearch(e.target.value); setBuyClientPage(1); }} style={{ width: '100%', marginBottom: 4 }} size="small" allowClear />
                  <div style={{ marginBottom: 4, display: 'flex', gap: 8 }}>
                    <Button size="small" onClick={() => setSelectedClients((buyGroupClients||[]).filter(c => (c.available || 0) > 0).map(c => c.client_id))}>全选</Button>
                    <Button size="small" onClick={() => setSelectedClients(prev => {
                      const allIds = (buyGroupClients||[]).map(c => c.client_id);
                      const inGroup = prev.filter(id => allIds.includes(id));
                      const outsideGroup = prev.filter(id => !allIds.includes(id));
                      const toggledInGroup = allIds.filter(id => !inGroup.includes(id));
                      return [...outsideGroup, ...toggledInGroup];
                    })}>反选</Button>
                  </div>
                  {(() => {
                    const pageSize = 10;
                    const filtered = buyGroupClients.filter(c => {
                      if (!buyClientSearch) return true;
                      const s = buyClientSearch.toLowerCase();
                      return (c.client_id || '').toLowerCase().includes(s) || (c.client_name || '').toLowerCase().includes(s);
                    });
                    const totalPages = Math.ceil(filtered.length / pageSize);
                    const p = Math.min(buyClientPage, Math.max(1, totalPages));
                    const paged = filtered.slice((p - 1) * pageSize, p * pageSize);
                    return paged.map(c => (
                      <div key={c.client_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                        <Checkbox checked={selectedClients.includes(c.client_id)}
                          disabled={c.available <= 0}
                          onChange={e => setSelectedClients(e.target.checked ? [...selectedClients, c.client_id] : selectedClients.filter(id => id !== c.client_id))} />
                        <span style={{ flex: 1 }}>{c.client_name || c.client_id}</span>
                        <span style={{ color: c.available > 0 ? '#3f8600' : '#999', fontSize: 12 }}>
                          {c.available > 0 ? `¥${c.available.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '无可用资金'}
                        </span>
                      </div>
                    ));
                  })()}
                </div>}
            </div>
            {(() => {
              const pageSize = 10;
              const filtered = buyGroupClients.filter(c => {
                if (!buyClientSearch) return true;
                const s = buyClientSearch.toLowerCase();
                return (c.client_id || '').toLowerCase().includes(s) || (c.client_name || '').toLowerCase().includes(s);
              });
              const totalPages = Math.ceil(filtered.length / pageSize);
              return (
              <div style={{ textAlign: 'center', margin: '6px 0' }}>
                <Button size="small" disabled={buyClientPage <= 1}
                  onClick={() => setBuyClientPage(p => Math.max(1, p - 1))}>上一页</Button>
                <span style={{ margin: '0 8px', fontSize: 12, color: '#888' }}>{buyClientPage}/{totalPages}</span>
                <Button size="small" disabled={buyClientPage >= totalPages}
                  onClick={() => setBuyClientPage(p => Math.min(totalPages, p + 1))}>下一页</Button>
              </div>);
            })()}
            {selectedClients.length > 0 && buyPrice > 0 && buyMode !== 'custom' && (
              <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, padding: 6, marginTop: 6, maxHeight: 160, overflow: 'auto' }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>仓位预览（{selectedClients.length}户）</div>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ color: '#888' }}>
                    <th style={{ textAlign: 'left', padding: '2px 4px' }}>客户</th>
                    <th style={{ textAlign: 'right', padding: '2px 4px' }}>数量</th>
                    <th style={{ textAlign: 'right', padding: '2px 4px' }}>金额</th>
                  </tr></thead>
                  <tbody>{selectedClients.map(cid => {
                    const c = buyGroupClients.find(x => x.client_id === cid);
                    if (!c) return null;
                    const qty = calcQty(c.available);
                    if (qty <= 0) return null;
                    return (
                      <tr key={cid}>
                        <td style={{ padding: '2px 4px', color: '#333' }}>{c.client_name || cid}</td>
                        <td style={{ textAlign: 'right', padding: '2px 4px' }}>{qty}</td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', color: '#3f8600' }}>¥{(qty * parseFloat(buyPrice)).toFixed(2)}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal title={<Space><BarChartOutlined /> {klineModal.stockName}({klineModal.stockCode})</Space>}
        open={klineModal.open} footer={null} width={750}
        onCancel={() => setKlineModal({ open: false, stockCode: '', stockName: '', data: [], loading: false })}
      >
        {klineModal.loading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          : klineModal.data.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无K线数据</div>
          : <ReactEChartsCore option={klineOption} style={{ height: 380 }} />}
      </Modal>
    </Card>
    </div>
  );
}
